import "@/App.css";
import "@/lib/i18n";
import { Component, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { applyTheme } from "@/lib/theme";
import { getProfile } from "@/lib/refData";
import { captureUtm, initErrorMonitor, initMarketing, trackEvent, metaTrack } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import HomePage from "@/pages/Home";
import AppShell from "@/components/AppShell";
import AuthCallback from "@/components/AuthCallback";

// In-app 404: unknown /app/* paths (stale bookmarks, typos) keep the
// authenticated shell instead of dropping the user onto the marketing site.
function AppNotFound() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white/70 p-10 text-center" data-testid="app-not-found">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">{t("nf_title")}</h1>
      <p className="mt-2 text-sm text-slate-500">{t("nf_hint")}</p>
      <Link to="/app" data-testid="app-not-found-home"
        className="mt-6 inline-flex min-h-[44px] items-center rounded-full bg-brand px-6 text-sm font-bold text-white active:scale-95">
        {t("nf_home")}
      </Link>
    </div>
  );
}

// Route-level code splitting: only Landing/Login/Signup/Home are in the
// initial bundle; everything else loads on demand (prefetched on idle).
const AppUpdates = lazy(() => import("@/pages/AppUpdates"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Legal = lazy(() => import("@/pages/Legal"));
const PartnerPortal = lazy(() => import("@/pages/PartnerPortal"));
const PartnerApply = lazy(() => import("@/pages/PartnerApply"));
const PartnerTerms = lazy(() => import("@/pages/PartnerTerms"));
const WhatsNext = lazy(() => import("@/pages/WhatsNext"));
const ConfirmSub = lazy(() => import("@/pages/ConfirmSub"));
const AdminOnboarding = lazy(() => import("@/pages/AdminOnboarding"));
const ImportData = lazy(() => import("@/pages/ImportData"));
const JobPage = lazy(() => import("@/pages/JobPage"));
const ManageShares = lazy(() => import("@/pages/ManageShares"));
const SharedGallery = lazy(() => import("@/pages/SharedGallery"));
const CrewCard = lazy(() => import("@/pages/CrewCard"));
const Projects = lazy(() => import("@/pages/Projects"));
const NewProject = lazy(() => import("@/pages/NewProject"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const Financials = lazy(() => import("@/pages/Financials"));
const Fleet = lazy(() => import("@/pages/Fleet"));
const Subs = lazy(() => import("@/pages/Subs"));
const SubPortal = lazy(() => import("@/pages/SubPortal"));
const AgentPortal = lazy(() => import("@/pages/AgentPortal"));
const JobCostReports = lazy(() => import("@/pages/JobCostReports"));
const EstimateEditor = lazy(() => import("@/pages/EstimateEditor"));
const EstimatePortal = lazy(() => import("@/pages/EstimatePortal"));
const ChangeOrderPortal = lazy(() => import("@/pages/ChangeOrderPortal"));
const InvoicePortal = lazy(() => import("@/pages/InvoicePortal"));
const DocSettings = lazy(() => import("@/pages/DocSettings"));
const InvoiceEditor = lazy(() => import("@/pages/InvoiceEditor"));
const DocDetail = lazy(() => import("@/pages/DocDetail"));
const ChangeOrderEditor = lazy(() => import("@/pages/ChangeOrderEditor"));
const TimeCard = lazy(() => import("@/pages/TimeCard"));
const Timecards = lazy(() => import("@/pages/Timecards"));
const BuildGuide = lazy(() => import("@/pages/BuildGuide"));
const BGVoice = lazy(() => import("@/pages/BGVoice"));
const AssistantActivity = lazy(() => import("@/pages/AssistantActivity"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Photos = lazy(() => import("@/pages/Photos"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const Settings = lazy(() => import("@/pages/Settings"));
const Trash = lazy(() => import("@/pages/Trash"));
const Crew = lazy(() => import("@/pages/Crew"));
const Billing = lazy(() => import("@/pages/Billing"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminBilling = lazy(() => import("@/pages/AdminBilling"));
const SupportAccess = lazy(() => import("@/pages/SupportAccess"));
const WebsiteEditor = lazy(() => import("@/pages/WebsiteEditor"));
const PublicSite = lazy(() => import("@/pages/PublicSite"));
const CcCustomerPage = lazy(() => import("@/pages/CcCustomerPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const Leads = lazy(() => import("@/pages/Leads"));
const Profile = lazy(() => import("@/pages/Profile"));
const Brand = lazy(() => import("@/pages/Brand"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const Help = lazy(() => import("@/pages/Help"));
const HomeownerPortal = lazy(() => import("@/pages/HomeownerPortal"));
const WalkthroughShare = lazy(() => import("@/pages/WalkthroughShare"));

function PageSkeleton() {
  return (
    <div className="space-y-4" data-testid="route-skeleton">
      <div className="bf-skeleton h-8 w-56" />
      <div className="bf-skeleton h-28" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="bf-skeleton h-24" />)}
      </div>
    </div>
  );
}

function FullPageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="app-loading">
      <div className="mx-auto max-w-3xl space-y-4 pt-10">
        <div className="bf-skeleton h-8 w-48" />
        <div className="bf-skeleton h-24" />
        <div className="grid grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="bf-skeleton h-28" />)}
        </div>
      </div>
    </div>
  );
}

const pub = (el) => <Suspense fallback={<FullPageSkeleton />}>{el}</Suspense>;

// Google OAuth returns to {origin}/app#session_id=... — detect the fragment
// during render (reactive location.hash) and exchange it BEFORE routing/auth.
function AuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

// Layout route: AppShell mounts ONCE for all /app pages — no more per-route
// shell remounts refetching profile/billing on every navigation.
function ProtectedLayout() {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <FullPageSkeleton />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return (
    <AppShell>
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function NotAuthorized() {
  const { t } = useTranslation();
  return (
    <div className="py-24 text-center" data-testid="route-denied">
      <h2 className="font-heading text-xl font-bold text-slate-900">{t("not_authorized")}</h2>
    </div>
  );
}

function RoleGate({ roles, superOnly = false, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <FullPageSkeleton />;
  if (!user) return null;
  if (superOnly ? !user.is_superadmin : (roles && !roles.includes(user.role))) return <NotAuthorized />;
  return children;
}

function ThemeSync() {
  const { user } = useAuth();
  useEffect(() => {
    let cached = "system";
    try { cached = localStorage.getItem("bp_theme") || "system"; } catch {}
    applyTheme(cached);
    if (!user) return;
    getProfile().then((data) => {
      const pref = data.theme || "system";
      try { localStorage.setItem("bp_theme", pref); } catch {}
      applyTheme(pref);
    }).catch(() => {});
  }, [user]);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) {
    try {
      import("@/lib/analytics").then(({ trackEvent }) =>
        trackEvent("client_error", { detail: `boundary:${String(err && err.message || "render").slice(0, 120)}`, once: false }));
    } catch { /* ignore */ }
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="min-h-screen bg-[#071A33] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="error-boundary">
          <div className="font-heading text-2xl font-extrabold text-white">BuildProject<span className="text-[#29C5F6]">360</span></div>
          <p className="text-sm text-slate-300 max-w-sm">Something went wrong loading this page. Your data is safe.</p>
          <button onClick={() => window.location.reload()} className="rounded-full bg-[#1473E6] px-6 py-3 text-sm font-bold text-white min-h-[44px]" data-testid="error-reload">Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Meta Pixel PageView on SPA route changes — the initial PageView fires once
// at pixel init, so the first effect run is skipped. No-op when no pixel ID.
let pvInitialized = false;
function MetaPageViews() {
  const loc = useLocation();
  useEffect(() => {
    if (!pvInitialized) { pvInitialized = true; return; }
    metaTrack("PageView");
  }, [loc.pathname]);
  return null;
}

function App() {
  // Prefetch the most likely next routes once the app is idle (chunks only —
  // no photo or project payloads are downloaded here).
  useEffect(() => {
    captureUtm();
    initErrorMonitor();
    initMarketing();
    const onPhotoUp = () => trackEvent("first_photo_uploaded");
    window.addEventListener("bf:photo-uploaded", onPhotoUp);
    const warm = () => {
      import("@/pages/Projects");
      import("@/pages/Photos");
      import("@/pages/Schedule");
      import("@/pages/ProjectDetail");
      import("@/pages/Customers");
    };
    if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 5000 });
    else setTimeout(warm, 3000);
    return () => window.removeEventListener("bf:photo-uploaded", onPhotoUp);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeSync />
        <MetaPageViews />
        <Toaster position="top-center" theme="dark" toastOptions={{ duration: 3000, style: { background: "#1E293B", color: "#F8FAFC", border: "1px solid #334155" } }} />
        <ErrorBoundary>
        <AuthGate>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/portal/:token" element={pub(<HomeownerPortal />)} />
          <Route path="/job/:token" element={pub(<JobPage />)} />
          <Route path="/share/walkthrough/:token" element={pub(<WalkthroughShare />)} />
          <Route path="/estimate/:token" element={pub(<EstimatePortal />)} />
          <Route path="/change-order/:token" element={pub(<ChangeOrderPortal />)} />
          <Route path="/invoice/:token" element={pub(<InvoicePortal />)} />
          <Route path="/support/:token" element={pub(<SupportAccess />)} />
          <Route path="/contractor/:slug" element={pub(<PublicSite />)} />
          <Route path="/partner/:token" element={pub(<PartnerPortal />)} />
          <Route path="/partner-signup" element={pub(<PartnerApply />)} />
          <Route path="/partner-terms" element={pub(<PartnerTerms />)} />
          <Route path="/next/:token" element={pub(<WhatsNext />)} />
          <Route path="/confirm-sub/:token" element={pub(<ConfirmSub />)} />
          <Route path="/sub/:token" element={pub(<SubPortal />)} />
          <Route path="/agent/:token" element={pub(<AgentPortal />)} />
          <Route path="/r/:token" element={pub(<CcCustomerPage />)} />
          <Route path="/payment/success" element={pub(<PaymentSuccess />)} />
          <Route path="/payment/cancel" element={pub(<PaymentCancel />)} />
          <Route path="/reset-password" element={pub(<ResetPassword />)} />
          <Route path="/verify-email" element={pub(<VerifyEmail />)} />
          <Route path="/terms" element={pub(<Legal kind="terms" />)} />
          <Route path="/privacy" element={pub(<Legal kind="privacy" />)} />
          <Route element={<ProtectedLayout />}>
            <Route path="/app" element={<HomePage />} />
            <Route path="/app/projects" element={<Projects />} />
            <Route path="/app/projects/new" element={<NewProject />} />
            <Route path="/app/customers" element={<Customers />} />
            <Route path="/app/customers/:id" element={<CustomerDetail />} />
            <Route path="/app/projects/:id" element={<ProjectDetail />} />
            <Route path="/app/photos" element={<Photos />} />
            <Route path="/app/schedule" element={<Schedule />} />
            <Route path="/app/time" element={<TimeCard />} />
            <Route path="/app/timecards" element={<RoleGate roles={["owner","admin"]}><Timecards /></RoleGate>} />
            <Route path="/app/assistant" element={<BuildGuide />} />
            <Route path="/app/assistant/voice" element={<BGVoice />} />
            <Route path="/app/assistant/activity" element={<AssistantActivity />} />
            <Route path="/app/crew" element={<RoleGate roles={["owner","admin"]}><Crew /></RoleGate>} />
            <Route path="/app/leads" element={<Leads />} />
            <Route path="/app/referrals" element={<RoleGate roles={["owner","admin"]}><Referrals /></RoleGate>} />
            <Route path="/app/help" element={<Help />} />
            <Route path="/app/billing" element={<RoleGate roles={["owner","admin"]}><Billing /></RoleGate>} />
            <Route path="/app/admin" element={<Admin />} />
            <Route path="/app/admin/billing" element={<RoleGate superOnly><AdminBilling /></RoleGate>} />
            <Route path="/app/profile" element={<Profile />} />
            <Route path="/app/brand" element={<Brand />} />
            <Route path="/app/app-updates" element={<AppUpdates />} />
            <Route path="/app/settings" element={<RoleGate roles={["owner","admin"]}><Settings /></RoleGate>} />
            <Route path="/app/settings/import" element={<RoleGate roles={["owner","admin"]}><ImportData /></RoleGate>} />
            <Route path="/app/settings/documents" element={<RoleGate roles={["owner","admin"]}><DocSettings /></RoleGate>} />
            <Route path="/app/trash" element={<Trash />} />
            <Route path="/app/camera" element={<Navigate to="/app/photos" replace />} />
            <Route path="/app/more" element={<Navigate to="/app" replace />} />
            <Route path="/app/integrations" element={<Integrations />} />
            <Route path="/app/admin/onboarding" element={<RoleGate superOnly><AdminOnboarding /></RoleGate>} />
            <Route path="/app/projects/:id/shares" element={<ManageShares />} />
            <Route path="/app/website" element={<WebsiteEditor />} />
            <Route path="/app/financials" element={<Financials />} />
            <Route path="/app/fleet" element={<Fleet />} />
            <Route path="/app/subs" element={<RoleGate roles={["owner","admin","office"]}><Subs /></RoleGate>} />
            <Route path="/app/money" element={<Navigate to="/app/financials" replace />} />
            <Route path="/app/financials/estimate/new" element={<EstimateEditor />} />
            <Route path="/app/financials/estimate/:id" element={<EstimateEditor />} />
            <Route path="/app/financials/invoice/new" element={<InvoiceEditor />} />
            <Route path="/app/financials/invoice/:id" element={<InvoiceEditor />} />
            <Route path="/app/financials/doc/:kind/:id" element={<DocDetail />} />
            <Route path="/app/reports" element={<RoleGate roles={["owner","admin"]}><JobCostReports /></RoleGate>} />
            <Route path="/app/projects/:projectId/change-order/new" element={<ChangeOrderEditor />} />
            <Route path="/app/financials/change-order/:id" element={<ChangeOrderEditor />} />
            <Route path="/app/*" element={<AppNotFound />} />
          </Route>
          <Route path="/s/:code" element={pub(<SharedGallery />)} />
          <Route path="/c/:codeToken" element={pub(<CrewCard />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthGate>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
